import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon } from 'lucide-react';
import type { SlideProps } from './SlideShell';
function formatEuro(value: number) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);
}
export function SlidePortfolioCostCalculator({}: SlideProps) {
  const [brands, setBrands] = useState(5);
  const [campaignsPerBrand, setCampaignsPerBrand] = useState(4);
  const [countries, setCountries] = useState(25);
  const costPerLocalizedCampaign = useMemo(() => {
    const masterBase = 2900;
    const masterFloor = 1900;
    const followUpMaster = 1000;
    const localizationBase = 999;
    const insightsBase = 99;
    const campaignDuration = 3;
    const totalCampaigns = brands * campaignsPerBrand;
    const masterProgress = Math.min(1, Math.max(0, (totalCampaigns - 1) / 99));
    const masterCost = masterBase - (masterBase - masterFloor) * masterProgress;
    const countryDiscount = Math.min(0.5, Math.max(0, (countries - 2) / 48 * 0.5));
    const localizationCost = localizationBase * (1 - countryDiscount);
    const insightsCost = insightsBase * (1 - countryDiscount);
    const perCampaignCountryCost = localizationCost + insightsCost * campaignDuration;
    const firstCampaigns = brands;
    const followUpCampaigns = Math.max(0, totalCampaigns - brands);
    const annualCost = firstCampaigns * (masterCost + perCampaignCountryCost * countries) + followUpCampaigns * (followUpMaster + perCampaignCountryCost * countries);
    const deployments = totalCampaigns * countries;
    return deployments > 0 ? annualCost / deployments : 0;
  }, [brands, campaignsPerBrand, countries]);
  const sliders = [{
    label: 'Brands',
    value: brands,
    min: 1,
    max: 40,
    onChange: setBrands
  }, {
    label: 'Campaigns per brand / year',
    value: campaignsPerBrand,
    min: 2,
    max: 12,
    onChange: setCampaignsPerBrand
  }, {
    label: 'Countries',
    value: countries,
    min: 1,
    max: 100,
    onChange: setCountries
  }];
  return <main className="relative flex h-full w-full overflow-hidden bg-[var(--brand)] text-white" data-id="element-123" data-mp="1s3c3w5af41h3">
      <div aria-hidden="true" className="absolute left-[52%] top-0 h-full w-px bg-white/10" data-id="element-124" data-mp="1x2ey0uov0naa" />

      <section className="relative z-10 flex w-[58%] flex-col justify-between px-[clamp(2.5rem,5vw,5.5rem)] py-[clamp(2.4rem,4.5vw,4.75rem)]" data-id="element-125" data-mp="1ib63c763obqa">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55
      }} data-id="element-126" data-mp="19a88aedn3eju">
          <div className="flex items-center gap-3" data-id="element-127" data-mp="1ddnzgv6xwmp5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--accent)] text-white shadow-lg" data-id="element-128" data-mp="zddajxp6xi6m">
              <SparklesIcon className="h-4 w-4" data-id="element-129" data-mp="14c6p7tg7rf8d" />
            </span>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/70" data-id="element-130" data-mp="2tq31q7o6cdab">
              The campaign cost disruptor
            </p>
          </div>

          <h1 className="mt-[clamp(2rem,4vw,4rem)] max-w-[52rem] font-serif font-semibold tracking-[-0.045em] text-white" data-id="element-131" data-mp="2ynbpcns3d9iy">
            <span className="block max-w-[42rem] text-[clamp(2.2rem,3.8vw,4rem)] leading-[1.02]" data-id="element-132" data-mp="i3hreqvnfn6s">
              A turn-key marketing campaign for
            </span>
            <span className="mb-8 mt-7 block text-[clamp(5.2rem,9vw,9rem)] leading-[0.82] text-[#F8D34A] drop-shadow-[0_8px_24px_rgba(248,211,74,0.18)]" data-id="element-133" data-mp="wu8hnuhlee7s">
              € 1000?
            </span>
          </h1>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.55
      }} className="my-auto max-w-[48rem]" data-id="element-134" data-mp="10tzfgi5jdult">
          <p className="max-w-[46rem] font-serif text-[clamp(2.2rem,3.8vw,4rem)] font-semibold leading-[1.02] tracking-[-0.035em] text-white" data-id="element-135" data-mp="1ardnpuhb3d31">
            So what happens when an agency is built around AI, not hours?
          </p>
          <p className="mt-6 max-w-[43rem] text-[clamp(0.85rem,1.15vw,1.05rem)] font-medium leading-relaxed text-white/55" data-id="element-136" data-mp="3ckl7bg3ycguq">
            Content creation, local adaptation, channel activation and insights, scaled across the portfolio. Media costs excluded.
          </p>
        </motion.div>

        <p className="max-w-2xl text-xs font-bold uppercase tracking-[0.18em] text-white/35" data-id="element-137" data-mp="2dlu7ajdjw184">
          Complete campaign delivery · Built to scale
        </p>
      </section>

      <motion.aside initial={{
      opacity: 0,
      x: 30
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="relative z-10 flex w-[42%] items-center px-[clamp(2rem,4vw,4.5rem)] py-[clamp(2.5rem,5vw,5rem)]" data-id="element-138" data-mp="1kwdfnnxr8ocy">
        <div className="w-full rounded-[2rem] border border-white/15 bg-white/[0.08] p-[clamp(1.5rem,3vw,3rem)] shadow-2xl backdrop-blur-sm" data-id="element-139" data-mp="1yn8kmp54ulb9">
          <div className="mb-7" data-id="element-140" data-mp="124auq0e2ylww">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--brand-lighter)]" data-id="element-141" data-mp="1239n65w9whjx">
              Break the agency model.
            </p>
            <h2 className="mt-2 font-serif text-[clamp(2rem,3vw,3rem)] font-semibold leading-tight text-white" data-id="element-142" data-mp="ynohuld6wwbd">
              Move the sliders.
            </h2>
          </div>

          <motion.div key={Math.round(costPerLocalizedCampaign)} initial={{
          opacity: 0.65,
          y: 5
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25
        }} className="mb-7 rounded-2xl border border-white/15 bg-white/10 px-5 py-4" aria-live="polite" data-id="element-143" data-mp="pmi1qdegdnin">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.15em] text-white/50" data-id="element-144" data-mp="ax2f99lyg48r">
              Modeled cost per executed, localized campaign*
            </p>
            <p className="mt-1 font-serif text-[clamp(2.5rem,4vw,4rem)] font-semibold leading-none tracking-tight text-white" data-id="element-145" data-mp="1xyrvtfr92og">
              {formatEuro(costPerLocalizedCampaign)}
            </p>
          </motion.div>

          <div className="space-y-7" data-id="element-146" data-mp="3o5oxe3ggk7xo">
            {sliders.map(slider => <div key={slider.label} data-id="element-147" data-mp="3egib4479aw53">
                <div className="mb-3 flex items-center justify-between gap-5" data-id="element-148" data-mp="3efgmewxbib4w">
                  <label className="text-sm font-semibold text-white/80" data-id="element-149" data-mp="3jgmgay2ps4g0">{slider.label}</label>
                  <output className="min-w-14 rounded-xl border border-white/15 bg-white/10 px-3 py-1.5 text-center text-lg font-bold text-white" data-id="element-150" data-mp="34jvs7kyhqf5p">
                    {slider.value}
                  </output>
                </div>
                <input type="range" min={slider.min} max={slider.max} value={slider.value} onChange={event => slider.onChange(Number(event.target.value))} className="h-2 w-full cursor-pointer appearance-none rounded-full bg-white/20 accent-[var(--accent)]" aria-label={slider.label} data-id="element-151" data-mp="13ygh7kokz59" />
                <div className="mt-2 flex justify-between text-[0.65rem] font-semibold text-white/40" data-id="element-152" data-mp="or68wox0gdpv">
                  <span data-id="element-153" data-mp="29hlg105ob7v8">{slider.min}</span>
                  <span data-id="element-154" data-mp="29gabuz8bfy3l">{slider.max}</span>
                </div>
              </div>)}
          </div>

          <p className="mt-7 text-xs font-medium leading-relaxed text-white/45" data-id="element-155" data-mp="24iia7dhvcxm7">
            *Media spend excluded. The more markets and campaigns activated, the more efficiently each country campaign can be executed.
          </p>
        </div>
      </motion.aside>
    </main>;
}